import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Messages.css';
import { useNotificationContext } from '../contexts/NotificationContext';

const Messages = () => {
  const { showSuccess, showError } = useNotificationContext();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState('all');

  const subjectLabels = {
    'support': 'Support technique',
    'bug': 'Signaler un bug',
    'feature': 'Demande de fonctionnalité',
    'account': 'Problème de compte',
    'other': 'Autre'
  };

  const statusLabels = {
    'new': 'Nouveau',
    'read': 'Lu',
    'replied': 'Répondu',
    'closed': 'Fermé'
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get('/api/contact');
      setMessages(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des messages:', error);
      showError('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  };

  const openMessage = async (message) => {
    setSelectedMessage(message);
    setShowModal(true);
    
    // Mark as read if not already
    if (!message.isRead) {
      try {
        await axios.put(`/api/contact/${message._id}`, { isRead: true });
        fetchMessages(); // Refresh to update read status
      } catch (error) {
        console.error('Erreur lors de la mise à jour:', error);
      }
    }
  };

  const updateStatus = async (messageId, status) => {
    try {
      await axios.put(`/api/contact/${messageId}`, { status });
      fetchMessages();
      showSuccess('Statut mis à jour');
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      showError('Erreur lors de la mise à jour');
    }
  };

  const deleteMessage = async (messageId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
      try {
        await axios.delete(`/api/contact/${messageId}`);
        fetchMessages();
        setShowModal(false);
        showSuccess('Message supprimé');
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        showError('Erreur lors de la suppression');
      }
    }
  };

  const filteredMessages = messages.filter(message => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !message.isRead;
    return message.status === filter;
  });

  const getStatusColor = (status) => {
    const colors = {
      'new': '#e74c3c',
      'read': '#f39c12',
      'replied': '#27ae60',
      'closed': '#95a5a6'
    };
    return colors[status] || '#95a5a6';
  };

  if (loading) return <div className="loading">Chargement...</div>;

  return (
    <div className="messages-page">
      <div className="page-header">
        <h2>Messages de Contact</h2>
        <div className="messages-stats">
          <span className="stat">
            Total: {messages.length}
          </span>
          <span className="stat unread">
            Non lus: {messages.filter(m => !m.isRead).length}
          </span>
        </div>
      </div>

      <div className="messages-filters">
        <button 
          className={filter === 'all' ? 'active' : ''}
          onClick={() => setFilter('all')}
        >
          Tous
        </button>
        <button 
          className={filter === 'unread' ? 'active' : ''}
          onClick={() => setFilter('unread')}
        >
          Non lus
        </button>
        <button 
          className={filter === 'new' ? 'active' : ''}
          onClick={() => setFilter('new')}
        >
          Nouveaux
        </button>
        <button 
          className={filter === 'replied' ? 'active' : ''}
          onClick={() => setFilter('replied')}
        >
          Répondus
        </button>
      </div>

      <div className="messages-list">
        {filteredMessages.length === 0 ? (
          <div className="no-messages">
            <p>Aucun message trouvé</p>
          </div>
        ) : (
          filteredMessages.map(message => (
            <div 
              key={message._id} 
              className={`message-card ${!message.isRead ? 'unread' : ''}`}
              onClick={() => openMessage(message)}
            >
              <div className="message-header">
                <div className="message-info">
                  <h3>{message.name}</h3>
                  <span className="message-email">{message.email}</span>
                </div>
                <div className="message-meta">
                  <span 
                    className="message-status"
                    style={{ backgroundColor: getStatusColor(message.status) }}
                  >
                    {statusLabels[message.status]}
                  </span>
                  <span className="message-date">
                    {new Date(message.createdAt).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              </div>
              <div className="message-subject">
                <strong>Sujet:</strong> {subjectLabels[message.subject]}
              </div>
              <div className="message-preview">
                {message.message.substring(0, 100)}...
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && selectedMessage && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content message-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Message de {selectedMessage.name}</h3>
              <button onClick={() => setShowModal(false)} className="modal-close">×</button>
            </div>
            
            <div className="message-details">
              <div className="detail-row">
                <strong>Email:</strong> {selectedMessage.email}
              </div>
              <div className="detail-row">
                <strong>Sujet:</strong> {subjectLabels[selectedMessage.subject]}
              </div>
              <div className="detail-row">
                <strong>Date:</strong> {new Date(selectedMessage.createdAt).toLocaleString('fr-FR')}
              </div>
              <div className="detail-row">
                <strong>Statut:</strong> 
                <select 
                  value={selectedMessage.status}
                  onChange={(e) => updateStatus(selectedMessage._id, e.target.value)}
                  className="status-select"
                >
                  <option value="new">Nouveau</option>
                  <option value="read">Lu</option>
                  <option value="replied">Répondu</option>
                  <option value="closed">Fermé</option>
                </select>
              </div>
            </div>
            
            <div className="message-content">
              <h4>Message:</h4>
              <p>{selectedMessage.message}</p>
            </div>
            
            <div className="modal-actions">
              <button 
                onClick={() => deleteMessage(selectedMessage._id)}
                className="btn-delete"
              >
                Supprimer
              </button>
              <button 
                onClick={() => setShowModal(false)}
                className="btn-close"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;